<template>
    <div>
        <head-layout :title="product.name" />

        <breadcrumbs :items="breadcrumbs" />

        <v-row>
            <v-col cols="12" md="4">
                <v-card>
                    <v-img
                        :src="product.image"
                        width="100%"
                        aspect-ratio="1.3"
                        contain
                    ></v-img>
                </v-card>
            </v-col>

            <v-col cols="12" md="8">
                <v-card>
                    <v-card-title
                        class="secondary--text"
                        v-text="product.name"
                    />

                    <v-card-subtitle>
                        <rental-rates :item="product" class="secondary--text" />
                        <rental-rates
                            :item="product"
                            inc-vat
                            class="primary--text"
                        >
                            <span class="text--lighten-4">inc. VAT</span>
                        </rental-rates>
                    </v-card-subtitle>

                    <v-card-text
                        style="white-space: pre-wrap"
                        v-text="product.description"
                    />
                </v-card>

                <v-card v-if="tabs.length" class="mt-6">
                    <v-tabs v-model="tab" grow>
                        <v-tab v-for="item in tabs" :key="item.title">
                            <v-avatar class="ma-2" tile size="24">
                                <v-img contain :src="item.icon" />
                            </v-avatar>
                            {{ item.title }}
                        </v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="tab">
                        <v-tab-item v-for="item in tabs" :key="item.title">
                            <v-card v-if="item.title === 'Specifications'">
                                <v-card-text>
                                    <ul>
                                        <li
                                            v-for="item in product.specifications"
                                        >
                                            <strong v-text="item.title" /><span
                                                v-if="item.title && item.value"
                                                >:</span
                                            >
                                            <span v-text="item.value" />
                                        </li>
                                    </ul>
                                </v-card-text>
                            </v-card>

                            <v-card v-if="item.title === 'Resources'">
                                <v-card-text v-if="product.resources">
                                    <v-list>
                                        <v-list-item
                                            v-for="(
                                                item, i
                                            ) in product.resources"
                                            :key="i"
                                            target="_blank"
                                            :href="item.url"
                                        >
                                            <v-list-item-icon>
                                                <v-icon>mdi-file</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content>
                                                <v-list-item-title
                                                    v-text="item.title"
                                                />
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </v-card-text>

                                <v-card-text
                                    v-for="video in product.videos"
                                    :key="video"
                                >
                                    <v-responsive :aspect-ratio="16 / 9">
                                        <iframe
                                            type="text/html"
                                            width="100%"
                                            height="100%"
                                            :src="`https://www.youtube.com/embed/${video}?autoplay=0&loop=1&playlist=${video}`"
                                            frameborder="0"
                                        />
                                    </v-responsive>
                                </v-card-text>
                            </v-card>
                        </v-tab-item>
                    </v-tabs-items>
                </v-card>
            </v-col>
        </v-row>

        <!--Alternatives-->
        <v-sheet
            v-if="product.alternatives"
            rounded
            color="grey lighten-5"
            elevation="1"
            class="my-8 pa-4"
        >
            <v-row>
                <v-col>
                    <div class="text-h4 primary--text">
                        Looking for something else:
                    </div>

                    <gallery-grid
                        :items="product.alternative_products"
                        :route-function="
                            (item) =>
                                route('catalogue.product', {
                                    department:
                                        item.product_group.department.slug,
                                    productGroup: item.product_group.slug,
                                    product: item.slug,
                                })
                        "
                    >
                        <template v-slot:subtitle="{ item }">
                            <rental-rates :item="item" />
                        </template>
                    </gallery-grid>
                </v-col>
            </v-row>
        </v-sheet>

        <v-row>
            <v-col cols="12" md="8">
                <contact-form
                    :origin="`Product Page: ${product.name}`"
                    hint="Don't forget to include dates if you'd like to check availability"
                    :message="`Hi, I'd like to know more about your ${product.name}...`"
                >
                    <div class="text-h4 primary--text">Get In Touch</div>
                </contact-form>
            </v-col>

            <v-col cols="12" md="4">
                <company-contact-info
                    large
                    style="height: 100%"
                    class="d-flex flex-column align-end justify-center"
                />
            </v-col>
        </v-row>
    </div>
</template>

<script>
import Breadcrumbs from "@/Components/Catalogue/Breadcrumbs";
import HeadLayout from "@/Layouts/HeadLayout";
import RentalRates from "@/Components/Catalogue/RentalRates";
import ContactForm from "@/Components/Catalogue/ContactForm";
import CompanyContactInfo from "@/Components/Catalogue/CompanyContactInfo";
import GalleryGrid from "@/Components/Catalogue/GalleryGrid";

export default {
    name: "Product",
    components: {
        GalleryGrid,
        CompanyContactInfo,
        ContactForm,
        RentalRates,
        HeadLayout,
        Breadcrumbs,
    },
    props: {
        product: {
            type: Object,
        },
    },

    data() {
        return {
            breadcrumbs: [
                {
                    text: "Hire Catalogue",
                    disabled: false,
                    href: "/",
                },
                {
                    text: this.$page.props.department.name,
                    disabled: false,
                    href: this.route(
                        "catalogue.department",
                        this.$page.props.department.slug
                    ),
                },
                {
                    text: this.$page.props.productGroup.name,
                    disabled: false,
                    href: this.route("catalogue.productGroup", [
                        this.$page.props.department.slug,
                        this.$page.props.productGroup.slug,
                    ]),
                },
                {
                    text: this.product.name,
                    disabled: true,
                    href: this.route("catalogue.product", [
                        this.$page.props.department.slug,
                        this.$page.props.productGroup.slug,
                        this.product.slug,
                    ]),
                },
            ],
            tab: null,
        };
    },

    computed: {
        tabs() {
            const tabs = [];

            if (this.product.specifications) {
                tabs.push({
                    title: "Specifications",
                    icon: this.$page.props.statics.specifications_logo,
                });
            }

            if (this.product.resources || this.product.videos) {
                tabs.push({
                    title: "Resources",
                    icon: this.$page.props.statics.resources_logo,
                });
            }

            return tabs;
        },

        priceForJsonLD() {
            if (
                this.product.hide_price ||
                (!this.product.day_rate && !this.product.week_rate)
            ) {
                return 0;
            }

            return this.product.day_rate || this.product.week_rate;
        },
    },

    jsonld() {
        const date = new Date();
        date.setFullYear(date.getFullYear() + 1);

        return {
            "@context": "https://schema.org/",
            "@type": "Product",
            name: this.product.name,
            brand: this.product.name,
            sku: this.product.name,
            image: this.product.image,
            description: this.product.description,
            offers: {
                "@type": "Offer",
                url: this.route("catalogue.product", [
                    this.$page.props.department.slug,
                    this.$page.props.productGroup.slug,
                    this.product.slug,
                ]),
                priceCurrency: "GBP",
                price: this.priceForJsonLD,
                availability: "https://schema.org/InStock",
                priceValidUntil: date,
            },
        };
    },
};
</script>

<style scoped></style>
